/**
 * Contributions-only fetch for the custom-model page (#2211).
 *
 * The generic ``useGetCustomModelsData`` reads ``offset`` / ``limit``
 * off the URL — that's fine for the Datasets tab's DataGrid, but the
 * Contributions tab needs *every* editor-clip row so the user can see
 * all 300+ contributions at once. Coupling to the shared paginated
 * fetch also meant that paging through Datasets sliced the list the
 * Contributions tab rendered from.
 *
 * This hook hits the same endpoint with a large ``limit`` and no
 * ``offset``, so it always returns the full contribution set for the
 * model regardless of what the user is doing on Datasets. Backend
 * currently returns all rows regardless, but declaring a large limit
 * keeps the hook correct if server-side pagination is added later.
 */

import { useQuery } from 'react-query';
import { AxiosError } from 'axios';
import { Axios } from '@utils/api/axios-instance.ts';
import { GET_URL_NEW } from '@utils/api/endpoints.ts';
import { BaseError } from '@utils/types/shared/base.ts';
import {
  CustomModels,
  CustomModelDataStatus
} from './use-get-custom-models-data.tsx';
import { CUSTOM_MODEL_QUERY_KEYS } from './query-keys';

const CONTRIBUTIONS_LIMIT = 5000;

export const useGetContributions = (id: number) => {
  const fetchAll = async () => {
    const { datasetsByModelId } = GET_URL_NEW();
    const url = datasetsByModelId(id, {
      offset: 0,
      limit: CONTRIBUTIONS_LIMIT
    });
    const { data } = await Axios.get<CustomModels>(url);
    return data;
  };

  return useQuery<CustomModels, AxiosError<BaseError>>(
    [...CUSTOM_MODEL_QUERY_KEYS.data(id), 'contributions-all'],
    fetchAll,
    {
      useErrorBoundary: false,
      // Poll while any row is still PROCESSING (new contributions land
      // as PROCESSING and flip to READY/FAILED asynchronously); stop
      // polling as soon as every row is in a terminal state.
      refetchInterval: data => {
        const hasPending = data?.result?.some(
          r => r.status === CustomModelDataStatus.PROCESSING
        );
        return hasPending ? 15000 : false;
      },
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: false,
      staleTime: 8000
    }
  );
};
